/**
 * MovementSystem — Handles entity movement each frame
 *
 * Queries entities with 'position' + 'velocity' components.
 * Special handling for:
 * - Player (uses InputAdapter)
 * - Chase behavior (enemies move toward player)
 * - Dash behavior (charges at player periodically)
 * - Orbit behavior (circles around player)
 * - Retreat+shoot behavior (keeps distance)
 * - World bounds clamping (player) / despawn (projectiles)
 */

export class MovementSystem {
  update(dt, entities, engine) {
    const player = entities.queryTag('player')[0];
    const playerPos = player ? entities.getComponent(player, 'position') : null;

    // Update all entities with position + velocity
    const movers = entities.query('position', 'velocity');

    for (const id of movers) {
      const pos = entities.getComponent(id, 'position');
      const vel = entities.getComponent(id, 'velocity');
      const behavior = entities.getComponent(id, 'behavior');

      if (entities.hasTag(id, 'player')) {
        // Player movement from input
        const move = engine.input.getMovement();
        pos.x += move.x * engine.playerStats.moveSpeed * dt;
        pos.y += move.y * engine.playerStats.moveSpeed * dt;

        // Track facing direction for auto-aim
        if (move.x !== 0 || move.y !== 0) {
          pos.angle = Math.atan2(move.y, move.x);
        }

        // Clamp to world bounds
        const margin = 15;
        pos.x = Math.max(margin, Math.min(engine.width - margin, pos.x));
        pos.y = Math.max(margin, Math.min(engine.height - margin, pos.y));

      } else if (behavior) {
        // AI behaviors
        this._updateBehavior(dt, id, pos, vel, behavior, playerPos, entities, engine);

      } else {
        // Simple velocity-based movement (projectiles, pickups)
        pos.x += vel.vx * dt;
        pos.y += vel.vy * dt;
      }

      // Despawn out-of-bounds projectiles
      if (entities.hasTag(id, 'bullet')) {
        const margin = 50;
        if (pos.x < -margin || pos.x > engine.width + margin ||
            pos.y < -margin || pos.y > engine.height + margin) {
          entities.destroy(id);
        }
      }
    }
  }

  _updateBehavior(dt, id, pos, vel, behavior, playerPos, entities, engine) {
    if (!playerPos) return;

    const dx = playerPos.x - pos.x;
    const dy = playerPos.y - pos.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dx / dist;
    const ny = dy / dist;

    switch (behavior.type) {
      case 'chase':
        vel.vx = nx * behavior.speed;
        vel.vy = ny * behavior.speed;
        pos.x += vel.vx * dt;
        pos.y += vel.vy * dt;
        pos.angle = Math.atan2(ny, nx);
        break;

      case 'dash':
        // Charge at player every dashInterval seconds
        behavior.dashTimer = (behavior.dashTimer || 0) + dt;
        if (behavior.dashTimer >= (behavior.dashInterval || 3)) {
          behavior.dashTimer = 0;
          behavior.dashing = true;
          behavior.dashVx = nx * behavior.speed * 3;
          behavior.dashVy = ny * behavior.speed * 3;
          behavior.dashDuration = 0.4;
        }
        if (behavior.dashing) {
          pos.x += behavior.dashVx * dt;
          pos.y += behavior.dashVy * dt;
          behavior.dashDuration -= dt;
          if (behavior.dashDuration <= 0) behavior.dashing = false;
        } else {
          // Slow drift toward player between dashes
          pos.x += nx * behavior.speed * 0.3 * dt;
          pos.y += ny * behavior.speed * 0.3 * dt;
        }
        pos.angle = Math.atan2(ny, nx);
        break;

      case 'orbit':
        // Circle around player
        behavior.orbitAngle = (behavior.orbitAngle || 0) + dt * 1.5;
        const orbitRadius = behavior.orbitRadius || 150;
        const targetX = playerPos.x + Math.cos(behavior.orbitAngle) * orbitRadius;
        const targetY = playerPos.y + Math.sin(behavior.orbitAngle) * orbitRadius;
        const tdx = targetX - pos.x;
        const tdy = targetY - pos.y;
        pos.x += tdx * 3 * dt;
        pos.y += tdy * 3 * dt;
        pos.angle = Math.atan2(tdy, tdx);
        break;

      case 'retreat_shoot':
        // Keep distance, periodically shoot
        const preferredDist = 200;
        if (dist < preferredDist) {
          pos.x -= nx * behavior.speed * dt;
          pos.y -= ny * behavior.speed * dt;
        } else if (dist > preferredDist + 50) {
          pos.x += nx * behavior.speed * 0.5 * dt;
          pos.y += ny * behavior.speed * 0.5 * dt;
        }
        pos.angle = Math.atan2(ny, nx);

        // Shoot
        behavior.shootTimer = (behavior.shootTimer || 0) + dt;
        if (behavior.shootTimer >= (behavior.shootInterval || 2)) {
          behavior.shootTimer = 0;
          this._spawnEnemyBullet(pos, playerPos, entities, engine);
        }
        break;

      case 'split':
        // Chase, but on death will split (handled in collision)
        vel.vx = nx * behavior.speed;
        vel.vy = ny * behavior.speed;
        pos.x += vel.vx * dt;
        pos.y += vel.vy * dt;
        pos.angle = Math.atan2(ny, nx);
        break;

      default:
        // Default: chase
        pos.x += nx * behavior.speed * dt;
        pos.y += ny * behavior.speed * dt;
        pos.angle = Math.atan2(ny, nx);
    }

    // Keep enemies on screen (with some margin)
    const margin = -30;
    pos.x = Math.max(margin, Math.min(engine.width - margin, pos.x));
    pos.y = Math.max(margin, Math.min(engine.height - margin, pos.y));
  }

  _spawnEnemyBullet(enemyPos, playerPos, entities, engine) {
    const dx = playerPos.x - enemyPos.x;
    const dy = playerPos.y - enemyPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;

    const bulletId = entities.create();
    entities.addComponent(bulletId, 'position', {
      x: enemyPos.x, y: enemyPos.y, angle: Math.atan2(dy, dx),
    });
    entities.addComponent(bulletId, 'velocity', {
      vx: (dx / dist) * 250,
      vy: (dy / dist) * 250,
    });
    entities.addComponent(bulletId, 'render', {
      shape: 'circle', size: 4, color: '#ff0040', glow: true,
    });
    entities.addComponent(bulletId, 'damage', { amount: 1, source: 'enemy' });
    entities.addTag(bulletId, 'enemyBullet');
    entities.addTag(bulletId, 'bullet');
  }
}
